import { getLanguageFromLocale } from '@/utils/translations/localizationHelpers';

import { Language } from '@/utils/translations/types/TypeLanguagesAndLocales';

/**
 * Supports localized formatting of possessive proper nouns i.e. (Your child's play kits).
 *
 * Example usage paired with `interpolate` function:
 *
 *   const childsPlayKitsEnglish = interpolate("{{childName}} Play Kits", {
 *      childName: formatPossessiveProperNoun("Arlo", "en-US"),
 *   });
 *   // result: "Arlo's Play Kits"
 *
 *   const childsPlayKitsDutch = interpolate("Speelkits {{childName}}", {
 *     childName: formatPossessiveProperNoun("Arlo", "nl"),
 *   });
 *   // result: "Speelkits van Arlo"
 */
export default function formatPossessiveProperNoun(
  noun: string,
  locale: string | undefined,
) {
  const language = getLanguageFromLocale(locale);

  switch (language) {
    case Language.French:
      const vowels = ['a', 'e', 'i', 'o', 'u'];
      const firstCharacter = noun?.[0]?.toLowerCase();
      const doesNounStartWithVowel = vowels.includes(firstCharacter);

      return doesNounStartWithVowel ? `d'${noun}` : `de ${noun}`;
    case Language.Spanish:
      return `de ${noun}`;
    case Language.Dutch:
      return `van ${noun}`;
    case Language.German:
      return `für ${noun}`;
    case Language.English:
      const lastCharacter = noun?.[noun.length - 1]?.toLowerCase();
      const doesNounEndWithS = lastCharacter === 's';

      return doesNounEndWithS ? `${noun}'` : `${noun}'s`;
    default:
      return noun;
  }
}
